import { Zone } from "@fairfleet/geotab";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { GridFilterInputMultipleSingleSelectProps } from "@mui/x-data-grid-pro";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounced } from "@/hooks/useDebounced";
import { useZonesBySearch } from "@/hooks/zone/useZonesBySearch";

/** The filter stores only id and name to keep the persisted filter model small. */
type ZoneOption = Pick<Zone, "id" | "name">;

const ZoneMultiSelectFilter: React.FC<GridFilterInputMultipleSingleSelectProps> = ({
  item,
  applyValue,
}) => {
  const [search, setSearch] = useState("");
  const searchDebounced = useDebounced(search, 1000);
  const { data: zones, isLoading } = useZonesBySearch(searchDebounced, 100);
  // Reduce amount of data necessary to be stored
  const zoneOptions = zones?.map(zone => ({
    id: zone.id,
    name: zone.name,
  }));
  const [selectedZones, setSelectedZones] = useState<ZoneOption[]>(item.value || []);

  useEffect(() => {
    applyValue({ ...item, value: selectedZones });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZones]);

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setSearch(newInputValue);
  };

  const handleZoneChange = (_event: React.SyntheticEvent, newValue: ZoneOption[]) => {
    setSelectedZones(newValue);
    applyValue({ ...item, value: newValue });
  };

  const { t } = useTranslation();

  return (
    <Autocomplete
      multiple
      options={zoneOptions || []}
      value={selectedZones}
      onChange={handleZoneChange}
      onInputChange={handleInputChange}
      disableCloseOnSelect
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name}
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant="standard"
          label="Zonen"
          placeholder={t("faultOverview.diagnosticPlaceholder")}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      loading={isLoading}
    />
  );
};

export default ZoneMultiSelectFilter;
